import React from 'react';
import ProductThumbnail from '../product/ProductThumbnail';
import { formatPrice } from '../../utils/formatPrice';
import { useLanguage } from '../../context/LanguageContext';
import {
  getBasePrice,
  getDisplayPrice,
  getSalePercentage,
  productHasSale,
} from '../../utils/productPricing';
import './CartModal.css';

const getCartItemId = (item) => item.id || item._id || item.productId;
const getCartItemQuantity = (item) => item.quantity || item.qty || 1;
const getCartItemSpecialCode = (item) => (
  item.specialCode ||
  item.product?.specialCode ||
  item.productCode ||
  item.product?.productCode ||
  item.code ||
  item.product?.code ||
  ''
);

const CartModal = ({
  isOpen,
  onClose,
  cartItems,
  cartTotal,
  onRemoveItem,
  onUpdateQuantity,
  onCheckout,
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--cart" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('cart.myCart')} ({cartItems?.length || 0})</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('common.close')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" />
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <div className="modal-content">
          {cartItems && cartItems.length > 0 ? (
            <>
              <div className="cart-items">
                {cartItems.map((item, index) => {
                  const itemId = getCartItemId(item);
                  const itemName = item.title || item.name || t('common.product');
                  const quantity = getCartItemQuantity(item);
                  const prescription = item.selectedPrescription || item.prescription;
                  const specialCode = getCartItemSpecialCode(item);
                  const product = item.product || item;
                  const itemPrice = getDisplayPrice(product);
                  const hasSale = productHasSale(product);

                  return (
                    <div key={itemId || `${itemName}-${index}`} className="cart-item">
                      <ProductThumbnail product={item} size="cart" className="cart-item__image" />
                      <div className="cart-item__details">
                        <h4 className="cart-item__name">{itemName}</h4>
                        {prescription && <p className="cart-item__option">{t('product.prescription')}: {prescription}</p>}
                        {specialCode && <p className="cart-item__option">{t('product.specialCode')}: {specialCode}</p>}
                        <p className="cart-item__price">{formatPrice(itemPrice)}</p>
                        {hasSale && (
                          <div className="cart-item__sale">
                            <span>{formatPrice(getBasePrice(product))}</span>
                            <strong>-{getSalePercentage(product)}%</strong>
                          </div>
                        )}
                        <div className="cart-item__qty-controls">
                          <button
                            type="button"
                            onClick={() => onUpdateQuantity && onUpdateQuantity(itemId, Math.max(1, quantity - 1))}
                            disabled={!onUpdateQuantity || !itemId || quantity <= 1}
                          >
                            -
                          </button>
                          <span>{t('cart.qty')}: {quantity}</span>
                          <button
                            type="button"
                            onClick={() => onUpdateQuantity && onUpdateQuantity(itemId, quantity + 1)}
                            disabled={!onUpdateQuantity || !itemId}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cart-item__remove"
                        aria-label={`Remove ${itemName} from cart`}
                        onClick={() => onRemoveItem && onRemoveItem(itemId)}
                        disabled={!onRemoveItem || !itemId}
                      >
                        x
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="modal-footer">
                <div className="cart-summary">
                  <div className="summary-row">
                    <span>{t('common.subtotal')}</span>
                    <span>{formatPrice(cartTotal?.subtotal || 0)}</span>
                  </div>
                  <div className="summary-row">
                    <strong>{t('common.total')}</strong>
                    <strong>{formatPrice(cartTotal?.total || 0)}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn--primary btn--full cart-checkout-button"
                  onClick={onCheckout}
                >
                  {t('cart.checkout')}
                </button>
              </div>
            </>
          ) : (
            <div className="empty-cart">
              <p>{t('cart.empty')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartModal;
